import { TypedDocumentNode, gql } from '@apollo/client';
import { useCallback } from 'react';

import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';

import { GamesNavigator as DumbGamesNavigator } from '@sorare/gameplay/src/components/seo/GamesNavigator';

import { useGamesNavigatorQuery } from 'hooks/useGamesNavigatorQuery';
import { playerFootballGameToGeneric } from 'pages/seo/player/utils';

import {
  PlayerGamesNavigatorQuery,
  PlayerGamesNavigatorQueryVariables,
} from './__generated__/index.graphql';

const PLAYER_GAMES_NAVIGATOR_QUERY = gql`
  query PlayerGamesNavigatorQuery(
    $slug: String!
    $firstPast: Int
    $lastPast: Int
    $afterPast: String
    $beforePast: String
    $firstFuture: Int
    $lastFuture: Int
    $afterFuture: String
    $beforeFuture: String
  ) {
    football {
      player(slug: $slug) {
        slug
        pastGames(
          first: $firstPast
          last: $lastPast
          after: $afterPast
          before: $beforePast
        ) {
          edges {
            cursor
          }
          nodes {
            id
            ...playerFootballGameToGeneric_game
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            endCursor
          }
        }
        futureGames(
          first: $firstFuture
          last: $lastFuture
          after: $afterFuture
          before: $beforeFuture
        ) {
          edges {
            cursor
          }
          nodes {
            id
            ...playerFootballGameToGeneric_game
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            endCursor
          }
        }
      }
    }
  }
  ${playerFootballGameToGeneric.fragments.game}
` as TypedDocumentNode<
  PlayerGamesNavigatorQuery,
  PlayerGamesNavigatorQueryVariables
>;

type Props = { playerSlug: string };

const PAGE_SIZE = 5;
export const GamesNavigator = ({ playerSlug }: Props) => {
  const openDialog = useOpenDialog();
  const { data, loading, page, loadMore } = useGamesNavigatorQuery(
    PLAYER_GAMES_NAVIGATOR_QUERY,
    {
      slug: playerSlug,
    },
    PAGE_SIZE
  );

  const loadPage = useCallback(
    (direction: 'previous' | 'next') => {
      loadMore(true, direction, {
        futureFirstCursor: data?.football.player.futureGames.edges[0]?.cursor,
        futureEndCursor: data?.football.player.futureGames.pageInfo.endCursor,
        pastFirstCursor: data?.football.player.pastGames.edges[0]?.cursor,
        pastEndCursor: data?.football.player.pastGames.pageInfo.endCursor,
      });
    },
    [data, loadMore]
  );

  const pastGames =
    page <= 0 ? data?.football.player.pastGames.nodes || [] : [];
  const futureGames =
    page >= 0 ? data?.football.player.futureGames.nodes || [] : [];

  return (
    <DumbGamesNavigator
      hasNextPage={
        !!data?.football.player.futureGames.pageInfo.hasNextPage ||
        !!data?.football.player.pastGames.pageInfo.hasPreviousPage
      }
      hasPreviousPage={
        !!data?.football.player.pastGames.pageInfo.hasNextPage ||
        !!data?.football.player.futureGames.pageInfo.hasPreviousPage
      }
      loading={loading}
      loadPage={loadPage}
      futureGames={futureGames.map(game =>
        playerFootballGameToGeneric({ game })
      )}
      pastGames={pastGames.map(game =>
        playerFootballGameToGeneric({
          game,
          onClick: id => openDialog(DialogKey.game, id),
        })
      )}
    />
  );
};
